import { LinearProgress, Typography } from '../primitives/index.js'
import { graphHooks } from '@tunasong/graph-lib/react'
import type { FilterFunc } from '@tunasong/models'
import { entitySort, isPersistedEntity, matchAny } from '@tunasong/models'
import { usePlugins } from '@tunasong/plugin-lib'
import type { Entity, Persisted } from '@tunasong/schemas'
import { entityTypeNames, isEntity, isFolderContainer } from '@tunasong/schemas'
import type { FC, MouseEvent } from 'react'
import { useCallback, useRef, useState } from 'react'
import { useDrop } from 'react-dnd'
import invariant from 'tiny-invariant'
import { ConfirmDialog } from '../dialog/index.js'
import type { EntityListProps } from '../entity/entity-list.js'
import type { EntityListItemProps } from '../entity/entity-list-item.js'
import { ListSkeleton } from '../entity/list-skeleton.js'
import { reorder } from '../entity/reorder.js'
import { useListKeyboardNav } from '../hooks/list-keyboard-nav.js'
import { useSortFilterOptions } from '../hooks/sort.js'
import { useNavigateToEntity } from '../navigation/navigate.js'
import type { DropTarget } from './constants.js'
import { DROP_BOTTOM } from './constants.js'
import DndDivider from './dnd-divider.js'
import { DraggableListItem } from './dnd-list-item.js'
import { List } from '../primitives/index.js'
import styles from './dnd-list.module.css'

export interface DndListProps extends Omit<EntityListProps, 'onChange'> {
  filter?: FilterFunc

  /** Placeholder when there are no items */
  placeholder?: string

  sort?: boolean

  /** With `sort`: list folders first in automatic sorts. @see FilteredParams.foldersFirst */
  foldersFirst?: boolean

  isLoading?: boolean

  isFetching?: boolean

  navigate?: boolean

  // The key to use for sorting, filtering etc.. If provided, the options will be persisted on the device for this key.
  optionsKey?: string

  /** Row layout, passed to every row. @see EntityListItemProps.layout */
  itemLayout?: EntityListItemProps['layout']

  /** `columns` rows only: show the type column. @see EntityListItemProps.showType */
  showItemType?: boolean

  isDropTarget?(props: { source?: Entity; target: Entity }): boolean

  onChange?(elements: Persisted<Entity>[]): void
}

/** Accept drop into folders, but nothing else for now */

export const DndList: FC<DndListProps> = props => {
  const {
    entities,
    actions = true,
    size = 'small',
    selected = [],
    navigate = true,
    isLoading = false,
    isFetching = false,
    optionsKey = 'default',
    placeholder = 'No items',
    sort = false,
    foldersFirst = false,
    itemLayout,
    showItemType,
    onOpen,
    onChange,
    filter: _filter,
    isDropTarget: _isDropTargetProp,
    showBreadcrumbs: _showBreadcrumbs,
    loading: _loading,
    searchable: _searchable,
    controls: _controls,

    ...restProps
  } = props
  const { moveEntity } = graphHooks.useEntityUtil()
  const navigateToEntity = useNavigateToEntity()
  const plugins = usePlugins('all')

  const isDropTargetDefault = useCallback(
    ({ source, target }: { source?: Entity; target: Entity }) => {
      const plugin = plugins?.find(p => p.node?.type === target.type)
      const allowChildrenTypes = plugin?.allowChildrenTypes

      if (!source?.type) {
        return false
      }
      let sourceMatch = false
      if (typeof allowChildrenTypes === 'boolean') {
        sourceMatch = allowChildrenTypes
      } else {
        sourceMatch = Boolean(allowChildrenTypes?.includes(source.type))
      }

      const targetMatch = matchAny(isFolderContainer, () => Boolean(allowChildrenTypes))(target)

      return sourceMatch && targetMatch
    },
    [plugins]
  )
  const isDropTarget: typeof isDropTargetDefault = props.isDropTarget ?? isDropTargetDefault

  /** Set to confirm message */
  const moveData = useRef<[Persisted<Entity>, Persisted<Entity>] | null>(null)
  const [moving, setMoving] = useState(false)
  const [confirm, setConfirm] = useState<null | string>(null)

  const handleCancel = useCallback(async () => setConfirm(null), [])

  const handleMove = useCallback(async () => {
    invariant(moveData.current, 'moveData is not set')
    invariant(moveData.current, 'moveData is not set')

    const [entity, newParent] = moveData.current
    setMoving(true)
    await moveEntity({ entity, newParent })
    setMoving(false)
    moveData.current = null
    setConfirm(null)
  }, [moveEntity])

  const handleDropped = useCallback(
    (source: Entity, target: DropTarget) => {
      if (isPersistedEntity(target) && isPersistedEntity(source) && isDropTarget({ source, target })) {
        setConfirm(`Move ${source.type} "${source.name}" to ${target.type} "${target.name}?"`)
        moveData.current = [source, target]
        return
      }

      if (onChange) {
        /** Re-order the list */
        const targetIndex = isEntity(target) ? entities.findIndex(e => e.id === target.id) : entities.length - 1
        const sourceIndex = entities.findIndex(e => e.id === source.id)
        onChange(reorder(entities, sourceIndex, targetIndex))
      }
    },
    [entities, isDropTarget, onChange]
  )

  const handleOpen = useCallback(
    (element: Persisted<Entity>) => (ev: MouseEvent) => {
      const navigationMode = ev.shiftKey ? 'dialog' : 'page'
      if (onOpen) {
        onOpen(element, { navigationMode })
      } else if (navigate) {
        navigateToEntity(element, { navigationMode })
      }
    },
    [navigate, navigateToEntity, onOpen]
  )

  const [{ isHovering: isHoveringBottom }, dropBottomRef] = useDrop({
    accept: entityTypeNames as never,
    drop: item => {
      handleDropped(item, DROP_BOTTOM)
    },
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    canDrop: (item: Entity) => true,
    collect: monitor => ({
      isHovering: monitor.isOver({ shallow: true }), // canDrop(monitor.getItem()) &&,
    }),
  })

  const { filter, sortOptions } = useSortFilterOptions(optionsKey)

  const filteredEntities = (filter.length > 0 ? entities.filter(e => filter.includes(e.type)) : entities).filter(
    e => !e.trash
  )

  const entitiesSorted = sort
    ? entitySort({ entities: filteredEntities, sortOptions, typeFilter: filter, foldersFirst })
    : filteredEntities

  const { listProps: keyboardListProps, getItemRef } = useListKeyboardNav()

  if (isLoading && (!entities || entities.length === 0)) {
    return <ListSkeleton />
  }
  if (entities.length === 0) {
    return (
      <div className={styles.emptyState}>
        <Typography variant="caption" className={styles.emptyText}>
          {placeholder}
        </Typography>
      </div>
    )
  }

  /* A plain list, not an ARIA menu: rows are entities to open. Everything rendered inside it is an
   * <li>; the drag/drop affordances are aria-hidden rows so they never count as items. The refetch
   * progress bar stays exposed. */
  return (
    <>
      <List {...keyboardListProps} {...restProps}>
        {entitiesSorted.map((element, index) => (
          <DraggableListItem
            key={element.id}
            size={size}
            selected={selected.includes(element)}
            actions={actions}
            layout={itemLayout}
            showType={showItemType}
            element={element}
            navRef={getItemRef(index)}
            onDropped={handleDropped}
            isDropTarget={isDropTarget}
            onClick={handleOpen(element)}
          />
        ))}
        {isHoveringBottom ? (
          <li aria-hidden="true">
            <DndDivider />
          </li>
        ) : null}
        <li aria-hidden="true" className={styles.dropSpacer} ref={dropBottomRef as never} />
        {isFetching && entities.length > 0 ? (
          <li>
            <LinearProgress />
          </li>
        ) : null}
      </List>
      <ConfirmDialog
        open={Boolean(confirm)}
        busy={moving}
        title={'Confirm move'}
        text={confirm ?? ''}
        onCancel={handleCancel}
        onConfirm={handleMove}
      />
    </>
  )
}
